import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";
import { tenantStoragePath } from "../tenantContext";

const DB_NAME = "bos-offline-cache";
const DB_VERSION = 1;
const STORE_NAME = "blobs";

// In-memory URL cache — rebuilt from IDB on initOfflineCache().
// Map<setlistId, Map<songId, { blobUrl: string, storagePath: string }>>
const _urlCache = new Map();

// ─── IDB helpers ──────────────────────────────────────────────────────────────

function idbReq(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

let _dbPromise = null;

function getDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      e.target.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = () => {
      _dbPromise = null;
      reject(req.error);
    };
  });
  return _dbPromise;
}

// ─── Storage path extraction ──────────────────────────────────────────────────

// Extracts the stable storage path (e.g. "bands/abc/sheet_music/file.pdf")
// from a Firebase download URL or gs:// URI. Used as the staleness key because
// signed download URLs rotate independently of the underlying file.
function extractStoragePath(url) {
  try {
    if (!url) return "";
    if (url.startsWith("gs://")) {
      const i = url.indexOf("/", 5);
      return i === -1 ? "" : decodeURIComponent(url.slice(i + 1));
    }
    const parsed = new URL(url);
    const marker = "/o/";
    const mi = parsed.pathname.indexOf(marker);
    if (mi === -1) return "";
    return decodeURIComponent(parsed.pathname.slice(mi + marker.length));
  } catch {
    return "";
  }
}

// ─── Hydration ────────────────────────────────────────────────────────────────

let _initPromise = null;

// Reads all IDB entries and populates the in-memory URL cache.
// Call once on workspace mount — idempotent, only runs once per page session.
// Returns a promise that resolves when hydration is complete (or silently failed).
export function initOfflineCache() {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    try {
      const db = await getDb();
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const [allKeys, allVals] = await Promise.all([
        idbReq(store.getAllKeys()),
        idbReq(store.getAll()),
      ]);
      for (let i = 0; i < allKeys.length; i++) {
        const key = allKeys[i];
        const { blob, storagePath } = allVals[i];
        const slash = key.indexOf("/");
        if (slash === -1) continue;
        const setlistId = key.slice(0, slash);
        const songId = key.slice(slash + 1);
        try {
          const blobUrl = URL.createObjectURL(blob);
          if (!_urlCache.has(setlistId)) _urlCache.set(setlistId, new Map());
          _urlCache.get(setlistId).set(songId, { blobUrl, storagePath });
        } catch {
          // Skip corrupt entries silently
        }
      }
    } catch (err) {
      console.warn("[offlineCache] IDB hydration failed:", err);
    }
  })();
  return _initPromise;
}

// ─── URL resolution ───────────────────────────────────────────────────────────

async function resolveUrl(song, tenantContext) {
  const existingUrl = song?.pdfUrl || song?.pdfPath || "";
  if (!existingUrl) throw new Error(`No PDF URL for ${song?.title || "song"}`);
  const storagePath = extractStoragePath(existingUrl);
  if (!storagePath) return { url: existingUrl, storagePath: "" };
  const fileName = storagePath.split("/").pop();
  const candidates = [
    ...new Set([
      storagePath,
      ...(fileName && tenantContext?.bandId
        ? [tenantStoragePath(tenantContext, "sheet_music", fileName)]
        : []),
    ]),
  ];
  for (const path of candidates) {
    try {
      const url = await getDownloadURL(ref(storage, path));
      return { url, storagePath: path };
    } catch {}
  }
  return { url: existingUrl, storagePath };
}

// ─── Cache operations ─────────────────────────────────────────────────────────

// Fetches a PDF as a blob and stores it in IDB + in-memory cache.
// Skips if the song is already cached with the same storage path.
// Re-caches if the PDF was replaced (storage path changed).
// Throws on network or resolution failure; callers decide whether to swallow.
export async function cacheSongAsBlob(setlistId, song, tenantContext) {
  const existing = _urlCache.get(setlistId)?.get(song.id);
  const currentStoragePath = extractStoragePath(song?.pdfUrl || song?.pdfPath || "");
  if (existing && existing.storagePath === currentStoragePath) return;

  const { url, storagePath } = await resolveUrl(song, tenantContext);
  const response = await fetch(url);
  const blob = await response.blob();

  // Persist to IDB — best-effort; in-memory cache still works if IDB fails.
  try {
    const db = await getDb();
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(
      { blob, storagePath, cachedAt: Date.now() },
      `${setlistId}/${song.id}`
    );
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("[offlineCache] IDB write failed:", err);
  }

  if (existing?.blobUrl) URL.revokeObjectURL(existing.blobUrl);

  const blobUrl = URL.createObjectURL(blob);
  if (!_urlCache.has(setlistId)) _urlCache.set(setlistId, new Map());
  _urlCache.get(setlistId).set(song.id, { blobUrl, storagePath });
}

export function getCachedBlobUrl(setlistId, songId) {
  return _urlCache.get(setlistId)?.get(songId)?.blobUrl ?? null;
}

export function hasAllCached(setlistId, songIds) {
  const bucket = _urlCache.get(setlistId);
  if (!bucket) return false;
  return songIds.every((id) => bucket.has(id));
}

export function hasSomeCached(setlistId) {
  const bucket = _urlCache.get(setlistId);
  return !!bucket && bucket.size > 0;
}

// Returns true if the setlist was previously prepared but is now out of date:
// either a song's PDF was re-uploaded (storage path changed) or a song was
// added to the setlist after the last prepare (missing from the bucket entirely).
// Synchronous — reads the in-memory cache only, no IDB round-trip.
export function hasStaleEntries(setlistId, songs) {
  const bucket = _urlCache.get(setlistId);
  if (!bucket) return false;
  return songs.some((song) => {
    const entry = bucket.get(song.id);
    if (!entry) return true; // added to setlist after last prepare
    const currentPath = extractStoragePath(song?.pdfUrl || song?.pdfPath || "");
    return currentPath && entry.storagePath !== currentPath;
  });
}

// Revokes all blob URLs and clears the in-memory cache.
// IDB entries are left intact — initOfflineCache() will re-hydrate on next call.
export function revokeAll() {
  for (const bucket of _urlCache.values()) {
    for (const { blobUrl } of bucket.values()) {
      if (typeof blobUrl === "string" && blobUrl.startsWith("blob:")) {
        URL.revokeObjectURL(blobUrl);
      }
    }
  }
  _urlCache.clear();
  _initPromise = null;
}
